import React, { useState, useEffect } from "react";
import { Book, Calendar, FlaskConical, Headset } from "lucide-react";
import VisionMissionQualitySection from "./VisionMissionQualitySection";
import LeadershipSection from "./LeadershipSection";
import AboutInstituteSection from "./AboutInstituteSection";
import AdmissionNoticeModal from "./AdmissionNoticeModal";

import hero1 from "../images/hero1.jpeg";
import hero3 from "../images/hero3.jpeg";
import hero4 from "../images/hero4.jpeg";

const sliderImages = [hero1, hero3, hero4];

const notificationsData = [
  "📢 Admission Notification 2026-27: D. Pharmacy applications open for Institute Level & Vacant Seats (24–26 Oct 2026)!",
  "Orientation Program for new students starts on 10th August.",
  "Internal Exams for Semester III and V begin from 15th September.",
  "Anti-Ragging Week awareness campaign scheduled for 1st to 7th August.",
  'Research Seminar on "Innovations in Pharma" on 22nd July at 11:00 AM.',
];

const quickLinksData = [
  { id: 1, title: "Programs", description: "Explore our undergraduate and graduate programs", url: "#", icon: Book },
  { id: 2, title: "Academic Calendar", description: "Important dates and events for current students", url: "#", icon: Calendar },
  { id: 3, title: "Research", description: "Discover our innovative research projects", url: "#", icon: FlaskConical },
  { id: 4, title: "Anti-Ragging Helpline", description: "Call us for support and safety", url: "#", icon: Headset },
];

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 5000); // Slower slide for better viewing — 5 seconds

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Official Admission Notification 2026-27 Modal & Floating Trigger */}
      <AdmissionNoticeModal />

      {/* ================= HERO SLIDER ================= */}
      <section className="relative h-screen max-h-[85vh] overflow-hidden">
        <div className="absolute inset-0">
          {sliderImages.map((img, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1500 ease-in-out ${
                index === currentSlide ? "opacity-100" : "opacity-0"
              }`}
            >
              <img
                src={img}
                alt={`College campus slide ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />
            </div>
          ))}
        </div>

        {/* Hero Content Overlay
        <div className="relative z-10 h-full flex items-center justify-center text-center px-6">
          <div className="max-w-5xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 drop-shadow-2xl leading-tight">
              Welcome to Sumatidevi Tompe Institute
            </h1>
            <p className="text-xl md:text-3xl text-white/90 mb-10 font-light max-w-3xl mx-auto">
              Empowering minds through quality education, innovation, and values
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a
                href="#programs"
                className="px-10 py-5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl text-lg transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Explore Programs
              </a>
              <a
                href="#about"
                className="px-10 py-5 bg-white/10 backdrop-blur-md hover:bg-white/20 text-white font-semibold rounded-xl text-lg border border-white/40 transition-all"
              >
                Discover More
              </a>
            </div>
          </div>
        </div> */}

        {/* Slider Dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-4 z-20">
          {sliderImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-4 h-4 rounded-full transition-all duration-300 ${
                index === currentSlide ? "bg-white scale-125" : "bg-white/50 hover:bg-white/80"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Imported Sections */}
      <VisionMissionQualitySection />
      <AboutInstituteSection />

      {/* ================= MAIN CONTENT ================= */}
      <div className="max-w-7xl mx-auto px-6 py-16 lg:py-24 grid lg:grid-cols-3 gap-12">
        {/* Left Column - Notifications + Quick Links */}
        <div className="lg:col-span-2 space-y-12">
          {/* Notifications - Modern Ticker Style */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="bg-indigo-800 text-white px-8 py-5 font-semibold text-lg flex items-center gap-3">
              <span className="text-2xl">🔔</span> Latest Notifications
            </div>
            <div className="p-6">
              <div className="animate-marquee whitespace-nowrap flex gap-12 text-gray-700 font-medium">
                {notificationsData.concat(notificationsData).map((text, i) => (
                  <div key={i} className="inline-flex items-center gap-3">
                    <span className="text-indigo-600">•</span> {text}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Links - Modern Cards */}
          <div className="space-y-8">
            <h3 className="text-3xl font-bold text-indigo-900 text-center md:text-left">
              Quick Access
            </h3>
            <div className="grid sm:grid-cols-2 gap-6">
              {quickLinksData.map((link) => (
                <a
                  key={link.id}
                  href={link.url}
                  className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 flex flex-col items-center text-center hover:-translate-y-2"
                >
                  <link.icon className="w-14 h-14 text-indigo-600 mb-6 group-hover:scale-110 transition-transform" />
                  <h4 className="text-xl font-semibold text-indigo-900 mb-3">{link.title}</h4>
                  <p className="text-gray-600">{link.description}</p>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Contact Info (kept simple & elegant) */}
        <div className="space-y-12">
          <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-8 shadow-xl border border-indigo-100">
            <h3 className="text-2xl font-bold text-indigo-900 mb-8 text-center">
              Contact Us
            </h3>

            <div className="space-y-6 text-gray-800">
              <div>
                <p className="font-semibold">Name</p>
                <p>Sangam Nimkar</p>
              </div>
              <div>
                <p className="font-semibold">Email</p>
                <a href="mailto:stipsr.admission@gmail.com" className="text-indigo-700 hover:underline">
                  stipsr.admission@gmail.com
                </a>
              </div>
              <div>
                <p className="font-semibold">Phone</p>
                <a href="tel:8551954921" className="text-indigo-700 hover:underline">
                  8551954921
                </a>
              </div>
              <div>
                <p className="font-semibold">Address</p>
                <p className="leading-relaxed">
                  G.S. Tompe Educational Campus,<br />
                  Nanori Road, Chandur Bazar,<br />
                  Amravati - 444704
                </p>
              </div>
            </div>
          </div>

          {/* Optional WhatsApp CTA */}
          <a
            href="https://chat.whatsapp.com/KZkdOH1gmt85ekIh0pe6AJ?mode=ac_t"
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-green-600 hover:bg-green-700 text-white text-center py-5 rounded-xl font-semibold text-lg shadow-lg transition-all flex items-center justify-center gap-3"
          >
            <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Join Official WhatsApp Group
          </a>
        </div>
      </div>

      <LeadershipSection />

      {/* Custom CSS for marquee animation */}
      <style jsx>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default Home;